import React, { useState, useEffect } from 'react';
import { Save, Globe, Clock, Languages, Ruler, DollarSign, HardDrive } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

const SystemParamsPage = () => {
  const { t, changeLanguage } = useTranslation();
  const { success, error: showError } = useNotification();
  const { hasPermission } = usePermission();
  const canEditParams = hasPermission('settings_params', 'edit');
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [setupCompleted, setSetupCompleted] = useState(false);

  // Load timezones on component mount
  useEffect(() => {
    fetch('/api/settings/timezones')
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') {
          setTimezones(res.data);
        }
      })
      .catch(err => console.error('Failed to load timezones:', err));
  }, []);

  // Load currencies on component mount
  useEffect(() => {
    fetch('/api/settings/currencies')
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') {
          setCurrencies(res.data);
        }
      })
      .catch(err => console.error('Failed to load currencies:', err));
  }, []);

  useEffect(() => {
    fetch('/api/settings/params')
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') {
          setParams(res.data);
          // Check if system is already initialized
          setSetupCompleted(res.data.setup_completed === '1');
        }
        setDataLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch system params:', err);
        setDataLoading(false);
      });
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!params) return;
    
    setIsSubmitting(true);
    setLoading(true);
    try {
      // Prepare data to send
      let dataToSend = { ...params };
      
      // If system is initialized, remove protected fields from submission
      if (setupCompleted) {
        const protectedFields = ['weight_unit', 'length_unit', 'currency'];
        protectedFields.forEach(field => {
          delete dataToSend[field];
        });
      }
      
      const res = await fetch('/api/settings/params/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });
      const result = await res.json();
      if (res.ok && result.status === 'success') {
        // Update language in the frontend if language was changed
        if (params.language) {
          changeLanguage(params.language);
        }
        success(t('settings.save_success_system_params'));
      } else {
        // Show backend error message if available
        showError(result.message || t('settings.save_failed_system_params'));
      }
    } catch (err) {
      showError(t('settings.save_failed_system_params'));
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // Show loading state while data is being fetched
  if (dataLoading || !params) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">{t('settings.loading_system_params')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('settings.system_parameters')}</h1>
        {canEditParams && (
        <button 
          onClick={handleSave}
          disabled={isSubmitting || loading}
          className={`bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''} disabled:opacity-50`}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('common.processing')}
            </span>
          ) : (
            <><Save size={18} />{loading ? t('settings.saving') : t('common.save')}</>
          )}
        </button>
      )}</div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <form onSubmit={handleSave} className="p-8 space-y-8">
          {/* Basic Localization */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-700 dark:text-slate-300 border-b pb-2">
              <Globe size={20} className="text-blue-500" />
              <span>{t('settings.basic_localization')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.timezone')}</label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-transparent dark:text-white"
                    value={params.timezone}
                    onChange={e => setParams({...params, timezone: e.target.value})}
                    required
                  >
                    {timezones.length > 0 ? (
                      timezones.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))
                    ) : (
                      <option value={params.timezone}>{params.timezone}</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.language')}</label>
                <div className="relative">
                  <Languages className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <select 
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-transparent dark:text-white"
                    value={params.language}
                    onChange={e => setParams({...params, language: e.target.value})}
                  >
                    <option value="en">English</option>
                    <option value="zh">{t('setup.chinese')}</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Immutable Units */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-700 dark:text-slate-300 border-b pb-2">
              <Ruler size={20} className="text-blue-500" />
              <span>{t('settings.measurement_units_immutable')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Weight Unit */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {t('settings.weight_unit')}
                  {setupCompleted && (
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">({t('settings.immutable_after_init')})</span>
                  )}
                </label>
                <select
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-transparent dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:dark:bg-slate-800"
                  value={params.weight_unit}
                  onChange={e => setParams({...params, weight_unit: e.target.value})}
                  disabled={setupCompleted}
                  required
                >
                  <option value="kgs">{t('settings.kgs_label')}</option>
                  <option value="lbs">{t('settings.lbs_label')}</option>
                </select>
              </div>
              
              {/* Length Unit */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {t('settings.length_unit')}
                  {setupCompleted && (
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">({t('settings.immutable_after_init')})</span>
                  )}
                </label>
                <select
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-transparent dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:dark:bg-slate-800"
                  value={params.length_unit}
                  onChange={e => setParams({...params, length_unit: e.target.value})}
                  disabled={setupCompleted}
                  required
                >
                  <option value="cm">{t('settings.cm_label')}</option>
                  <option value="inch">{t('settings.inch_label')}</option>
                </select>
              </div>
              
              {/* Currency Unit */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {t('settings.currency')}
                  {setupCompleted && (
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">({t('settings.immutable_after_init')})</span>
                  )}
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-transparent dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:dark:bg-slate-800"
                    value={params.currency}
                    onChange={e => setParams({...params, currency: e.target.value})}
                    disabled={setupCompleted}
                    required
                  >
                    {currencies.length > 0 ? (
                      currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </option>
                      ))
                    ) : (
                      <option value={params.currency}>{params.currency}</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            {setupCompleted && (
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                {t('settings.immutable_units_note')}
              </div>
            )}
          </section>

          {/* Domain & Media */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-700 dark:text-slate-300 border-b pb-2">
              <Globe size={20} className="text-blue-500" />
              <span>{t('settings.domain_and_media_settings')}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.system_domain')}</label>
                <input 
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                  value={params.domain}
                  onChange={e => setParams({...params, domain: e.target.value})}
                  placeholder="eg: www.eoato.com"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-400">{t('settings.media_resource_domain')}</label>
                <input 
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                  value={params.media_domain}
                  onChange={e => setParams({...params, media_domain: e.target.value})}
                  placeholder="eg: https://www.eoato.com/media/"
                  required
                />
              </div>
            </div>
          </section>

          {/* Storage Type */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-700 dark:text-slate-300 border-b pb-2">
              <HardDrive size={20} className="text-blue-500" />
              <span>{t('settings.storage_configuration')}</span>
            </div>
            <div className="space-y-4">
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="storage" 
                    value="file" 
                    checked={params.storage_type === 'file'}
                    onChange={e => setParams({...params, storage_type: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">{t('settings.local_storage_label')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="storage" 
                    value="remote" 
                    checked={params.storage_type === 'remote'}
                    onChange={e => setParams({...params, storage_type: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">{t('settings.remote_storage_label')}</span>
                </label>
              </div>

              {params.storage_type === 'remote' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100 dark:border-blue-800 animate-in fade-in slide-in-from-top-2">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase">{t('settings.s3_bucket_name')}</label>
                    <input 
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:text-white"
                      value={params.s3_bucket}
                      onChange={e => setParams({...params, s3_bucket: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase">{t('settings.s3_endpoint_url')}</label>
                    <input 
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:text-white"
                      value={params.s3_endpoint}
                      onChange={e => setParams({...params, s3_endpoint: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase">{t('settings.access_key')}</label>
                    <input 
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:text-white"
                      value={params.s3_access_key}
                      onChange={e => setParams({...params, s3_access_key: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase">{t('settings.secret_key')}</label>
                    <input 
                      type="password"
                      className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 dark:text-white"
                      value={params.s3_secret_key}
                      onChange={e => setParams({...params, s3_secret_key: e.target.value})}
                    />
                  </div>
                </div>
              )}
            </div>
          </section>
        </form>
      </div>
    </div>
  );
};

export default SystemParamsPage;
